import { Utils } from './Utils';
import { PaymentInfo } from '../models/models';

/**
 * Display-ready expense receipt data as ESC/POS XML.
 *
 * Mirrors PrintExpensePaymentComponent
 * (src/app/pages/print-expense-payment) so the USB ticket prints the same
 * numbers as the browser receipt.
 */
export class ExpenseTicketBuilder {

	ticket_a_columns: number = 42;
	ticket_b_columns: number = 50;
	font_row: string = 'b';

	private payment_info: PaymentInfo;

	constructor(payment_info: PaymentInfo, ticket_a_columns: number = 42, ticket_b_columns: number = 50) {
		this.payment_info = payment_info;
		this.ticket_a_columns = ticket_a_columns;
		this.ticket_b_columns = ticket_b_columns;
	}

	private getEncoderMaxColumns(font_name: 'a' | 'b' = 'b'): number {
		return font_name == 'a' ? this.ticket_a_columns : this.ticket_b_columns;
	}

	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	private formatMoney(amount: number): string {
		return Utils.formatMoney(amount || 0);
	}

	private formatLongDate(value: any): string {
		if (!value) return '';
		const d = value instanceof Date ? value : new Date(value);
		if (isNaN(d.getTime())) return '';
		const month = d.toLocaleDateString('es-MX', { month: 'long' });
		return `${month} ${d.getDate()}, ${d.getFullYear()}`;
	}

	private formatTime(value: any): string {
		if (!value) return '';
		const d = value instanceof Date ? value : new Date(value);
		if (isNaN(d.getTime())) return '';
		const hours = d.getHours();
		const h = hours % 12 === 0 ? 12 : hours % 12;
		const m = d.getMinutes() < 10 ? '0' + d.getMinutes() : '' + d.getMinutes();
		return `${h}:${m} ${hours >= 12 ? 'p. m.' : 'a. m.'}`;
	}

	private buildTotalsRow(label: string, value: string, maxCols: number, bold: boolean = false): string {
		const dots = '.'.repeat(Math.max(0, maxCols - label.length - value.length - 2));

		if (bold) {
			return `<Text font="a" bold="true">${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text><Feed lines="1"></Feed>\n`;
		}
		return `<Text font="a">${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text><Feed lines="1"></Feed>\n`;
	}

	private buildSectionTitle(title: string): string {
		return `<Text justification="center" bold="true">${this.escapeXml(title)}</Text>\n<Feed lines="1"></Feed>\n`;
	}

	public buildXml(): string {
		let xml = '<EscPos>\n';
		xml += '<Init></Init>\n';
		xml += this.buildHeader();
		xml += this.buildBody();
		xml += this.buildFooter();
		xml += '<Feed lines="2"></Feed>\n';
		xml += '<Cut mode="partial"></Cut>\n';
		xml += '</EscPos>';
		return xml;
	}

	private buildHeader(): string {
		let xml = '';
		const store = this.payment_info?.store;

		if (store?.name) {
			xml += `<Text justification="center" bold="true">Sucursal ${this.escapeXml(store.name)}</Text>\n`;
		}
		if (store?.business_name) {
			xml += `<Text justification="center">${this.escapeXml(store.business_name)}</Text>\n`;
		}
		if (store?.rfc) {
			xml += `<Text justification="center">${this.escapeXml(store.rfc)}</Text>\n`;
		}
		const parts = [store?.address, store?.city, store?.state, store?.zipcode]
			.filter(p => !!p)
			.map(p => this.escapeXml(p as string));
		if (parts.length) {
			xml += `<Text justification="center">${parts.join(' ')}</Text>\n`;
		}
		if (store?.phone) {
			xml += `<Text justification="center">Tel: ${this.escapeXml(store.phone)}</Text>\n`;
		}

		xml += '<Feed lines="1"></Feed>\n';
		xml += this.buildSectionTitle('RECIBO DE GASTO');
		return xml;
	}

	private buildBody(): string {
		const maxCols = this.getEncoderMaxColumns('a');
		const payment = this.payment_info?.payment;
		const created_by = this.payment_info?.created_by_user?.name || '';
		let xml = '';

		xml += `<Text font="b" justification="left">Folio: </Text><Text font="b" bold="true" justification="left">#${payment?.id ?? ''}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">Fecha: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatLongDate(payment?.created) + ', ' + this.formatTime(payment?.created))}</Text><Feed lines="1"></Feed>\n`;
		if (created_by) {
			xml += `<Text font="b" justification="left">Atendio: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(created_by)}</Text><Feed lines="1"></Feed>\n`;
		}
		if (payment?.tag) {
			xml += `<Text font="b" justification="left">Tipo: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(payment.tag)}</Text><Feed lines="1"></Feed>\n`;
		}
		if (payment?.concept) {
			xml += `<Text font="b" justification="left">Concepto: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(payment.concept)}</Text><Feed lines="1"></Feed>\n`;
		}
		xml += '<Feed lines="1"></Feed>\n';
		xml += this.buildTotalsRow('Monto', `${this.formatMoney(payment?.payment_amount)} ${payment?.currency_id || ''}`.trim(), maxCols, true);

		return xml;
	}

	private buildFooter(): string {
		let xml = '<Feed lines="1"></Feed>\n';
		xml += `<Text justification="center">El pago por la cantidad de: ${this.escapeXml(this.formatMoney(this.payment_info?.payment?.payment_amount))}</Text>\n`;
		xml += `<Text justification="center">Solo es valido cuando presenta la firma del Gerente</Text>\n`;
		xml += '<Feed lines="2"></Feed>\n';
		xml += this.buildSectionTitle('Recibio:');
		xml += '<Feed lines="1"></Feed>\n';
		xml += `<Text justification="center">________________________________</Text>\n`;
		xml += '<Feed lines="1"></Feed>\n';
		return xml;
	}
}
